import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Upload, Plus, Trash2, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

export function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <input className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function TextAreaField({ label, value, onChange, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
  return (
    <Field label={label}>
      <textarea className="input" rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <Field label={label}>
      <input className="input" type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  );
}

export function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="color"
          value={value ?? '#000000'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 40, height: 36, padding: 2, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}
        />
        <input className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} style={{ flex: 1 }} />
      </div>
    </Field>
  );
}

export function ToggleField({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <span className="field-label" style={{ marginBottom: 0 }}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        style={{
          width: 42,
          height: 24,
          borderRadius: 999,
          border: 'none',
          cursor: 'pointer',
          background: value ? 'var(--accent)' : 'var(--border)',
          position: 'relative',
          flexShrink: 0,
        }}
        aria-pressed={value}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: value ? 20 : 2,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.15s ease',
          }}
        />
      </button>
    </div>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <Field label={label}>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

export function ImageField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function onFile(file: File) {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/uploads', { method: 'POST', credentials: 'include', body: form });
      if (!res.ok) throw new Error('upload failed');
      const data = await res.json();
      onChange(data.url);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {value ? (
          <img src={value} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 8, border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
            <Upload size={18} />
          </div>
        )}
        <input className="input" placeholder="URL de l'image" value={value ?? ''} onChange={(e) => onChange(e.target.value)} style={{ flex: 1 }} />
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
        />
        <button type="button" className="btn btn-secondary" style={{ width: 'auto', padding: '9px 14px' }} onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? '…' : 'Choisir'}
        </button>
      </div>
    </Field>
  );
}

export function ListEditor<T>({
  items,
  onChange,
  renderItem,
  newItem,
  itemLabel,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  renderItem: (item: T, update: (patch: Partial<T>) => void, index: number) => ReactNode;
  newItem: () => T;
  itemLabel: string;
}) {
  function update(index: number, patch: Partial<T>) {
    const next = items.slice();
    next[index] = { ...next[index], ...patch };
    onChange(next);
  }
  function remove(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }
  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  const [dragIndex, setDragIndex] = useState<number | null>(null);

  function onDrop(e: DragEvent, targetIndex: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === targetIndex) return;
    const next = items.slice();
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    onChange(next);
    setDragIndex(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item, i) => (
        <div
          key={i}
          className="card"
          style={{ display: 'flex', gap: 10, alignItems: 'flex-start', opacity: dragIndex === i ? 0.5 : 1 }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDrop(e, i)}
        >
          <span
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragEnd={() => setDragIndex(null)}
            style={{ cursor: 'grab', marginTop: 10, flexShrink: 0, display: 'flex' }}
            aria-hidden="true"
          >
            <GripVertical size={16} color="var(--text-tertiary)" />
          </span>
          <div style={{ flex: 1 }}>{renderItem(item, (patch) => update(i, patch), i)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <button type="button" onClick={() => move(i, -1)} className="btn btn-secondary" style={{ width: 32, height: 32, padding: 0 }} aria-label="Monter">
              <ArrowUp size={14} />
            </button>
            <button type="button" onClick={() => move(i, 1)} className="btn btn-secondary" style={{ width: 32, height: 32, padding: 0 }} aria-label="Descendre">
              <ArrowDown size={14} />
            </button>
            <button type="button" onClick={() => remove(i)} className="btn btn-danger-outline" style={{ width: 32, height: 32, padding: 0 }} aria-label="Supprimer">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-secondary" onClick={() => onChange([...items, newItem()])}>
        <Plus size={15} /> Ajouter {itemLabel}
      </button>
    </div>
  );
}
